// The check-in outbox — BP 23, 2026-09-21.
//
// A student presses "Check in" once, in a room of 77 people on one college
// wifi access point, and then puts the phone in a pocket. If that single POST
// fails, the old page showed a red message to somebody who had already stopped
// looking, and the attendance was simply gone. There is no second chance: the
// class moves on and nobody discovers the hole until grades are due.
//
// So the press is written to localStorage BEFORE the network is touched, and
// the network is treated as something that will fail. Every pending item is
// retried until the server accepts it or refuses it for a reason that retrying
// cannot fix. The student sees "saved on this phone" rather than an error,
// because from their side it is saved.
//
// WHAT LIVES ON THE PHONE. While an item is pending it holds the full student
// ID, the typed name and the code, because the retry has to be able to send
// them again. That is the student's own identity on the student's own device,
// which is not what migration 024 is about — 024 is about what the platform's
// database holds, and the database still only ever receives the reduced form.
// The moment the server confirms, those three fields are dropped and only the
// masked ref survives, so a shared or borrowed phone is not left holding a
// student number.
//
// Everything here is wrapped in try/catch. localStorage throws in a private
// window, when site data is blocked, and when the quota is full, and a student
// whose browser refuses storage must still be able to check in — they simply
// lose the retry safety net, which is strictly better than losing the page.

const KEY = 'ace.checkin.outbox.v1';

/** Keep the device's history bounded; a term is ~26 classes. */
const MAX_ITEMS = 60;

export type OutboxState = 'pending' | 'confirmed' | 'refused';

export interface OutboxItem {
  /** Local id. Not the server's — the server dedupes on its own key. */
  id: string;
  /** Wall-clock of the press, for display only. Never sent: see `pressedAt`. */
  at: string;
  /**
   * `Date.now()` at the press. Only ever used as a difference against a later
   * `Date.now()`, so a phone with a wrong clock still reports the right age.
   */
  pressedAt: number;
  state: OutboxState;
  tries: number;
  session_label?: string | null;

  /** Present only while pending — dropped the moment the server confirms. */
  student_id?: string;
  name?: string;
  code?: string;

  /** Present once confirmed. */
  ref?: string;
  /** Position in the class register: "you are #23". See markConfirmed. */
  seq?: number;
  /** How many had checked in when this was recorded. */
  total?: number;
  already?: boolean;
  /** When the register says it was recorded. Server clock when `serverTimed`. */
  confirmedAt?: string;
  /** True when `confirmedAt` came from the server rather than this phone. */
  serverTimed?: boolean;

  /** Present once refused, and the last transient error while pending. */
  error?: string;
}

function read(): OutboxItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as OutboxItem[]) : [];
  } catch {
    // Unparseable or unreadable. An empty outbox is recoverable; throwing here
    // would take the whole check-in page down with it.
    return [];
  }
}

function write(items: OutboxItem[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items.slice(-MAX_ITEMS)));
  } catch {
    // Quota or a blocked store. The in-memory copy still drives this session.
  }
}

/** True when the browser will actually keep what we write. */
export function storageWorks(): boolean {
  try {
    const probe = `${KEY}.probe`;
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function loadOutbox(): OutboxItem[] {
  return read();
}

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Record a press. Call this BEFORE the network request, so a submission that
 * dies in flight — or a tab closed a half-second later — is already on disk.
 */
export function enqueue(input: {
  student_id: string;
  name: string;
  code: string;
  session_label?: string | null;
}): OutboxItem {
  const item: OutboxItem = {
    id: newId(),
    at: new Date().toISOString(),
    pressedAt: Date.now(),
    state: 'pending',
    tries: 0,
    ...input,
  };
  write([...read(), item]);
  return item;
}

function patch(id: string, fn: (it: OutboxItem) => OutboxItem): OutboxItem[] {
  const next = read().map((it) => (it.id === id ? fn(it) : it));
  write(next);
  return next;
}

/**
 * The server took it. Drop the identity fields — only the receipt is needed.
 *
 * `serverAt` is the time the register itself holds. Prefer it over this
 * phone's clock: it is the one part of the receipt that does not depend on the
 * device being right, which is the whole reason it is worth showing.
 */
export function markConfirmed(
  id: string, ref: string, already: boolean, serverAt?: string,
  seq?: number, total?: number,
): OutboxItem[] {
  return patch(id, (it) => {
    const { student_id: _s, name: _n, code: _c, ...rest } = it;
    return {
      ...rest,
      state: 'confirmed',
      ref,
      already,
      confirmedAt: serverAt || new Date().toISOString(),
      serverTimed: !!serverAt,
      seq,
      total,
      error: undefined,
    };
  });
}

/**
 * The server refused for a reason retrying cannot fix — a mistyped ID, a wrong
 * code, a class that was not running. Kept visible rather than deleted: a
 * student who believes they checked in needs to be able to see that they did
 * not, and to show the instructor what they typed.
 */
export function markRefused(id: string, error: string): OutboxItem[] {
  return patch(id, (it) => ({ ...it, state: 'refused', error }));
}

/** Still pending, but this attempt failed. Keeps it queued. */
export function markTried(id: string, error?: string): OutboxItem[] {
  return patch(id, (it) => ({ ...it, tries: it.tries + 1, error }));
}

export function pendingItems(items: OutboxItem[] = read()): OutboxItem[] {
  return items.filter((it) => it.state === 'pending');
}

/** How long this item has been waiting, for the server's retry grace. */
export function msAgo(item: OutboxItem, now = Date.now()): number {
  return Math.max(0, now - item.pressedAt);
}

/**
 * Everything on this device, as text a student can paste into an email to the
 * instructor or a reader can read off the screen. This is the extraction path
 * that does not need the server to be up.
 */
export function toText(items: OutboxItem[] = read()): string {
  const lines = [
    'ACE Core Chemistry (ACEH-3006) — check-ins saved on this device',
    `Exported ${new Date().toLocaleString()}`,
    '',
  ];
  if (items.length === 0) lines.push('(nothing recorded on this device)');
  for (const it of items) {
    const when = new Date(it.at).toLocaleString();
    const what =
      it.state === 'confirmed'
        ? `CONFIRMED  #${it.seq ?? '?'}  ref ${it.ref}${it.already ? ' (already in)' : ''}`
        : it.state === 'pending' ? `NOT SENT YET  ${it.tries} attempt${it.tries === 1 ? '' : 's'}`
          : `REFUSED  ${it.error || ''}`;
    lines.push(`${when}  ${it.session_label || 'class'}  —  ${what}`);
  }
  return lines.join('\n');
}

/** Hand the text to the student as a file, so it survives a cleared browser. */
export function downloadText(items: OutboxItem[] = read()): void {
  try {
    const blob = new Blob([toText(items)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ace-checkins-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    // Download blocked. The same text is on screen to read or copy.
  }
}

/**
 * Which server refusals are final.
 *
 * Anything not named here — a dropped connection, a 500, a 429 from 77 phones
 * hitting one NAT, a backend restarting mid-class — is transient and stays
 * queued. Getting this list wrong in the safe direction costs a retry; getting
 * it wrong in the other direction loses the attendance, so the default for an
 * unrecognised failure is to keep trying.
 */
const FINAL_ERRORS = new Set([
  'invalid_student_id',   // the ID is not 9 digits starting 100 — retrying cannot fix it
  'name_required',
  'bad_code',             // judged at the press, so this really was the wrong code
  'attempt_too_old',      // held past the server's grace
  // Also judged at the press, so it is deterministic: if no window was open at
  // the instant the button went down, no amount of retrying changes that, and
  // churning for the whole grace period would put 77 phones into the rate
  // limiter for nothing. The item stays on screen for the instructor to see.
  'no_window_open',
]);

export function isFinalError(code: string | undefined): boolean {
  return !!code && FINAL_ERRORS.has(code);
}
